import {DICTIONARY} from '../../../../dictionary';
import {RouteService} from '../../../../services/routeService';
import {NotificationService} from '../../../../services/notificationService';
import {GeneralService} from '../../../../services/generalService';
import {Component, OnInit} from "@angular/core";
import {ENV_CONSTS} from "../../../../constants";


@Component({
	selector: 'account-graphql-component',
	templateUrl: './account-graphql.component.html',
})
export class AccountGraphqlComponent implements OnInit {
    dic = DICTIONARY;
    queryInput = '';
    highlightedQuery = '';
    jsonNodes = [];
    queryInProcess = false;
    collapsedIds = new Set();
    myAccountApi = null;

    activePanel: 'docs' | 'history' | 'explorer' | null = null;
    docsLoading = false;
    schemaData = null;
    schemaTypes = [];
    selectedType = null;
    docsRootCollapsed = false;
    docsTypesCollapsed = false;

    queryHistory = [];

    explorerTree = [];
    explorerOperationName = 'MyQuery';
    explorerInitialized = false;

    showAutocomplete = false;
    autocompleteItems = [];
    autocompleteFilter = '';
    acSelectedIdx = 0;
    acPosition = {top: 0, left: 0};
    acCursorPos = 0;

    constructor(private gs:GeneralService,
				private rs:RouteService,
				private ns:NotificationService) {
    }

	ngOnInit() {
        // CC: taken from account-api.component.ts -> ngOnInit
        this.rs.getAccountInfo().then((accountInfo) => {
            this.myAccountApi = accountInfo.api;
        });
    }

    executeQuery = () => {
        if (!this.queryInput) {
            return;
        }
        this.prettifyQuery();
        this.queryInProcess = true;
        this.jsonNodes = [];
        this.collapsedIds = new Set();
        this.rs.getGraphql(this.queryInput).then((response) => {
            this.jsonNodes = this.buildJsonTree(response);
            this.queryInProcess = false;
            this.addToHistory(this.queryInput);
        }, () => {
            this.queryInProcess = false;
        });
    };

    addToHistory = (query) => {
        const trimmed = query.trim();
        if (!trimmed) {
            return;
        }
        this.queryHistory = [
            {query: trimmed, timestamp: new Date()},
            ...this.queryHistory.filter(h => h.query !== trimmed)
        ].slice(0, 20);
    };

    loadFromHistory = (item) => {
        this.queryInput = item.query;
        this.highlightedQuery = this.highlightGraphql(item.query);
    };

    setPanel = (panel) => {
        this.activePanel = this.activePanel === panel ? null : panel;
        if (this.activePanel && !this.schemaData && !this.docsLoading) {
            this.loadSchema();
        }
        if (this.activePanel === 'explorer' && this.schemaData && !this.explorerInitialized) {
            this.initExplorerTree();
        }
    };

    loadSchema = () => {
        this.docsLoading = true;
        const introspectionQuery = '{ __schema { queryType { name } mutationType { name } subscriptionType { name } types { name kind description fields(includeDeprecated: false) { name description type { name kind ofType { name kind } } } } } }';
        this.rs.getGraphql(introspectionQuery).then((response) => {
            const schema = response?.data?.__schema || response?.__schema || null;
            this.schemaData = schema;
            this.schemaTypes = schema?.types?.filter(t => !t.name.startsWith('__')) || [];
            this.docsLoading = false;
            if (this.activePanel === 'explorer') {
                this.initExplorerTree();
            }
        }, () => {
            this.docsLoading = false;
        });
    };

    // CC: explorer — builds a two-level field tree from the root query type
    initExplorerTree = () => {
        if (!this.schemaData) {
            return;
        }
        const rootTypeName = this.schemaData.queryType?.name || 'Query';
        const rootType = this.schemaData.types?.find(t => t.name === rootTypeName);
        if (!rootType?.fields) {
            return;
        }
        this.explorerTree = rootType.fields.map(field => {
            const baseTypeName = this.getBaseTypeName(field.type);
            const subType = this.schemaData.types?.find(t => t.name === baseTypeName);
            const hasChildren = !!(subType?.fields?.length);
            const children = hasChildren
                ? subType.fields.map(subField => ({
                    name: subField.name,
                    typeName: this.getBaseTypeName(subField.type),
                    selected: false
                }))
                : null;
            return {
                name: field.name,
                typeName: baseTypeName,
                hasChildren,
                expanded: false,
                selected: false,
                children
            };
        });
        this.explorerInitialized = true;
    };

    onExplorerParentClick = (node) => {
        node.expanded = !node.expanded;
        if (node.expanded && node.children) {
            const hasAnySelected = node.children.some(c => c.selected);
            if (!hasAnySelected) {
                node.children[0].selected = true;
            }
        }
        this.buildQueryFromTree();
    };

    onExplorerChildClick = (child) => {
        child.selected = !child.selected;
        this.buildQueryFromTree();
    };

    onExplorerLeafClick = (node) => {
        node.selected = !node.selected;
        this.buildQueryFromTree();
    };

    buildQueryFromTree = () => {
        const lines = [];
        this.explorerTree.forEach(node => {
            if (node.hasChildren) {
                const selectedChildren = node.children?.filter(c => c.selected) || [];
                if (!selectedChildren.length) {
                    return;
                }
                lines.push('  ' + node.name + ' {');
                selectedChildren.forEach(child => {
                    lines.push('    ' + child.name);
                });
                lines.push('  }');
            }
            else if (node.selected) {
                lines.push('  ' + node.name);
            }
        });

        if (!lines.length) {
            this.queryInput = '';
            this.highlightedQuery = '';
            return;
        }
        const query = `query ${this.explorerOperationName} {\n${lines.join('\n')}\n}`;
        this.queryInput = query;
        this.highlightedQuery = this.highlightGraphql(query);
    };

    selectType = (typeName) => {
        this.selectedType = this.schemaData?.types?.find(t => t.name === typeName) || null;
    };

    backToDocs = () => {
        this.selectedType = null;
    };

    formatTypeRef = (typeRef) => {
        if (!typeRef) {
            return '';
        }
        if (typeRef.kind === 'NON_NULL') {
            return this.formatTypeRef(typeRef.ofType) + '!';
        }
        if (typeRef.kind === 'LIST') {
            return '[' + this.formatTypeRef(typeRef.ofType) + ']';
        }
        return typeRef.name || '';
    };

    // CC: autocomplete — keyboard navigation
    onKeyDown = (event, textareaEl) => {
        if (!this.showAutocomplete) {
            return;
        }
        if (event.key === 'ArrowDown') {
            event.preventDefault();
            this.acSelectedIdx = Math.min(this.acSelectedIdx + 1, this.autocompleteItems.length - 1);
        }
        else if (event.key === 'ArrowUp') {
            event.preventDefault();
            this.acSelectedIdx = Math.max(this.acSelectedIdx - 1, 0);
        }
        else if (event.key === 'Enter' || event.key === 'Tab') {
            event.preventDefault();
            this.applyAutocomplete(this.autocompleteItems[this.acSelectedIdx], textareaEl);
        }
        else if (event.key === 'Escape') {
            event.preventDefault();
            this.showAutocomplete = false;
        }
    };

    onEditorBlur = () => {
        this.showAutocomplete = false;
        this.prettifyQuery();
    };

    onKeyUp = (event, textareaEl) => {
        if (['ArrowDown', 'ArrowUp', 'Escape'].includes(event.key)) {
            return;
        }
        if (this.showAutocomplete && (event.key === 'Enter' || event.key === 'Tab')) {
            return;
        }
        this.updateAutocomplete(textareaEl);
    };

    updateAutocomplete = (textareaEl) => {
        if (!this.schemaData) {
            if (!this.docsLoading) {
                this.loadSchema();
            }
            this.showAutocomplete = false;
            return;
        }

        const cursorPos = textareaEl.selectionStart;
        const textBefore = this.queryInput.substring(0, cursorPos);

        // Get the identifier being typed right before the cursor
        const wordMatch = textBefore.match(/[a-zA-Z_][a-zA-Z0-9_]*$/);
        const currentWord = wordMatch ? wordMatch[0] : '';

        // Only trigger inside a selection set (must have an unclosed {)
        const textForContext = textBefore.substring(0, textBefore.length - currentWord.length);
        const openBraces = (textForContext.match(/\{/g) || []).length;
        const closeBraces = (textForContext.match(/\}/g) || []).length;
        if (openBraces <= closeBraces) {
            this.showAutocomplete = false;
            return;
        }

        // Determine the current type context from text before the word
        const currentTypeName = this.getTypeAtCursor(textForContext);
        if (!currentTypeName) {
            this.showAutocomplete = false;
            return;
        }

        const typeObj = this.schemaData.types?.find(t => t.name === currentTypeName);
        if (!typeObj?.fields?.length) {
            this.showAutocomplete = false;
            return;
        }

        const items = typeObj.fields
            .filter(f => f.name.toLowerCase().startsWith(currentWord.toLowerCase()))
            .map(f => ({name: f.name, type: this.formatTypeRef(f.type), description: f.description}));

        if (!items.length) {
            this.showAutocomplete = false;
            return;
        }

        this.autocompleteItems = items;
        this.autocompleteFilter = currentWord;
        this.acSelectedIdx = 0;
        this.acCursorPos = cursorPos;
        this.acPosition = this.getCaretPos(textareaEl);
        this.showAutocomplete = true;
    };

    applyAutocomplete = (item, textareaEl) => {
        if (!item) {
            return;
        }
        const before = this.queryInput.substring(0, this.acCursorPos - this.autocompleteFilter.length);
        const after = this.queryInput.substring(this.acCursorPos);
        this.queryInput = before + item.name + after;
        this.highlightedQuery = this.highlightGraphql(this.queryInput);
        this.showAutocomplete = false;

        const newPos = before.length + item.name.length;
        setTimeout(() => {
            textareaEl.setSelectionRange(newPos, newPos);
            textareaEl.focus();
        });
    };

    // CC: walks tokens before cursor to determine the current GraphQL type context
    getTypeAtCursor = (textBefore) => {
        const cleaned = textBefore
            .replace(/#[^\n]*/g, ' ')
            .replace(/"[^"]*"/g, '""');

        const re = /\.\.\.|[a-zA-Z_][a-zA-Z0-9_]*|[{}():]/g;
        const tokens = [];
        let m;
        while ((m = re.exec(cleaned)) !== null) {
            tokens.push(m[0]);
        }

        let rootTypeName = this.schemaData?.queryType?.name || 'Query';
        let i = 0;

        if (['query', 'mutation', 'subscription'].includes(tokens[i])) {
            if (tokens[i] === 'mutation') {
                rootTypeName = this.schemaData?.mutationType?.name || 'Mutation';
            }
            else if (tokens[i] === 'subscription') {
                rootTypeName = this.schemaData?.subscriptionType?.name || 'Subscription';
            }
            i++;
            // Skip optional operation name
            if (tokens[i] && tokens[i] !== '{' && tokens[i] !== '(') {
                i++;
            }
            // Skip optional variable definitions
            if (tokens[i] === '(') {
                let d = 1; i++;
                while (i < tokens.length && d > 0) {
                    if (tokens[i] === '(') { d++; }
                    if (tokens[i] === ')') { d--; }
                    i++;
                }
            }
        }
        else if (tokens[i] === 'fragment') {
            i += 4; // fragment Name on TypeName
        }

        const typeStack = [];
        if (tokens[i] === '{') {
            typeStack.push(rootTypeName);
            i++;
        }

        while (i < tokens.length) {
            const token = tokens[i];

            if (token === '}') {
                typeStack.pop();
                i++;
                continue;
            }

            if (token === '{') {
                i++;
                continue;
            }

            if (token === '...') {
                i++;
                if (tokens[i] === 'on') {
                    i++;
                    const inlineType = tokens[i]; i++;
                    if (tokens[i] === '{') {
                        typeStack.push(inlineType);
                        i++;
                    }
                }
                else {
                    i++; // skip fragment name
                }
                continue;
            }

            // Field or alias
            let fieldName = token;
            i++;

            // Alias: alias: realFieldName
            if (tokens[i] === ':') {
                i++;
                fieldName = tokens[i] || fieldName;
                i++;
            }

            // Skip arguments
            if (tokens[i] === '(') {
                let d = 1; i++;
                while (i < tokens.length && d > 0) {
                    if (tokens[i] === '(') { d++; }
                    if (tokens[i] === ')') { d--; }
                    i++;
                }
            }

            // Sub-selection
            if (tokens[i] === '{') {
                const currentType = typeStack[typeStack.length - 1] || rootTypeName;
                const resolvedType = this.resolveFieldType(currentType, fieldName);
                typeStack.push(resolvedType || '?');
                i++;
            }
        }

        return typeStack[typeStack.length - 1] || null;
    };

    resolveFieldType = (typeName, fieldName) => {
        const typeObj = this.schemaData?.types?.find(t => t.name === typeName);
        if (!typeObj?.fields) {
            return null;
        }
        const field = typeObj.fields.find(f => f.name === fieldName);
        return field ? this.getBaseTypeName(field.type) : null;
    };

    getBaseTypeName = (typeRef) => {
        if (!typeRef) {
            return null;
        }
        if (typeRef.name) {
            return typeRef.name;
        }
        return this.getBaseTypeName(typeRef.ofType);
    };

    // CC: estimates caret pixel position using line/col and fixed font metrics
    getCaretPos = (textareaEl) => {
        const pos = textareaEl.selectionStart;
        const text = textareaEl.value.substring(0, pos - this.autocompleteFilter.length);
        const lines = text.split('\n');
        const lineNum = lines.length - 1;
        const colNum = lines[lineNum].length;

        const lineHeight = 22;
        const charWidth = 7.8;
        const padding = 16;

        const rect = textareaEl.getBoundingClientRect();
        const top = rect.top + padding + (lineNum * lineHeight) - textareaEl.scrollTop + lineHeight;
        const left = rect.left + padding + (colNum * charWidth);

        return {top, left};
    };

    prettifyQuery = () => {
        if (!this.queryInput.trim()) {
            return;
        }
        const tokens = this.queryInput
            .replace(/\{/g, ' { ')
            .replace(/\}/g, ' } ')
            .replace(/\s+/g, ' ')
            .trim()
            .split(' ')
            .filter(t => t.length > 0);

        let depth = 0;
        const lines = [];
        const headerTokens = [];
        let inHeader = true;

        for (let i = 0; i < tokens.length; i++) {
            const token = tokens[i];
            const next = tokens[i + 1];

            if (inHeader) {
                if (token === '{') {
                    lines.push((headerTokens.length ? headerTokens.join(' ') + ' ' : '') + '{');
                    headerTokens.length = 0;
                    depth++;
                    inHeader = false;
                }
                else {
                    headerTokens.push(token);
                }
                continue;
            }

            if (token === '}') {
                depth = Math.max(0, depth - 1);
                lines.push('  '.repeat(depth) + '}');
            }
            else if (next === '{') {
                lines.push('  '.repeat(depth) + token + ' {');
                depth++;
                i++;
            }
            else {
                lines.push('  '.repeat(depth) + token);
            }
        }

        const prettified = lines.join('\n');
        this.queryInput = prettified;
        this.highlightedQuery = this.highlightGraphql(prettified);
    };

    copyQuery = () => {
        if (!this.queryInput) {
            return;
        }
        navigator.clipboard.writeText(this.queryInput).then(() => {
            this.ns.showInfoMessage('Query copied to clipboard');
        });
    };

    copyUrlRequest = () => {
        if (!this.queryInput) {
            return;
        }
        const url = `${ENV_CONSTS.beBaseUrl}/admin/graphql?query=` + encodeURIComponent(this.queryInput);
        const key = this.myAccountApi?.key || '';
        const secret = this.myAccountApi?.secret || '';
        const curl = `curl '${url}' \\\n  -H 'x-trustifi-key: ${key}' \\\n  -H 'x-trustifi-secret: ${secret}'`;
        navigator.clipboard.writeText(curl).then(() => {
            this.ns.showInfoMessage('URL request copied to clipboard');
        });
    };

    onQueryChange = (value) => {
        this.highlightedQuery = this.highlightGraphql(value);
    };

    syncScroll = (event, backdropEl) => {
        backdropEl.scrollTop = event.target.scrollTop;
        backdropEl.scrollLeft = event.target.scrollLeft;
    };

    highlightGraphql = (text) => {
        const keywords = new Set(['query', 'mutation', 'subscription', 'fragment', 'on']);
        const escape = (s) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
        const result = [];
        let i = 0;
        let expectName = false;

        while (i < text.length) {
            const ch = text[i];

            if (ch === ' ' || ch === '\t' || ch === '\n' || ch === '\r') {
                result.push(ch);
                i++;
                continue;
            }

            if (ch === '#') {
                let j = i;
                while (j < text.length && text[j] !== '\n') { j++; }
                result.push(`<span style="color:#57606a">${escape(text.slice(i, j))}</span>`);
                i = j;
                continue;
            }

            if (ch === '"') {
                let j = i + 1;
                while (j < text.length && text[j] !== '"') {
                    if (text[j] === '\\') { j++; }
                    j++;
                }
                result.push(`<span style="color:#0a3069">${escape(text.slice(i, j + 1))}</span>`);
                i = j + 1;
                expectName = false;
                continue;
            }

            if (/[a-zA-Z_]/.test(ch)) {
                let j = i;
                while (j < text.length && /[a-zA-Z0-9_]/.test(text[j])) { j++; }
                const word = text.slice(i, j);
                if (keywords.has(word)) {
                    result.push(`<span style="color:#cf222e">${escape(word)}</span>`);
                    expectName = true;
                }
                else if (expectName) {
                    result.push(`<span style="color:#116329">${escape(word)}</span>`);
                    expectName = false;
                }
                else {
                    result.push(`<span style="color:#0550ae">${escape(word)}</span>`);
                }
                i = j;
                continue;
            }

            if (ch === '{' || ch === '}') {
                result.push(`<span style="color:#57606a">${ch}</span>`);
                expectName = false;
                i++;
                continue;
            }

            result.push(escape(ch));
            expectName = false;
            i++;
        }

        return result.join('');
    };

    buildJsonTree = (data) => {
        const nodes = [];
        let idCounter = 0;

        const walk = (value, key, depth, parentIds) => {
            if (value === null || typeof value !== 'object') {
                nodes.push({id: idCounter++, depth, key, type: 'value', value, valueType: value === null ? 'null' : typeof value, parentIds: [...parentIds]});
                return;
            }
            const isArray = Array.isArray(value);
            const myId = idCounter++;
            nodes.push({id: myId, depth, key, type: isArray ? 'array-open' : 'object-open', openChar: isArray ? '[' : '{', closeChar: isArray ? ']' : '}', childCount: isArray ? value.length : Object.keys(value).length, parentIds: [...parentIds]});
            const childParentIds = [...parentIds, myId];
            if (isArray) {
                value.forEach((item) => { walk(item, null, depth + 1, childParentIds); });
            }
            else {
                Object.entries(value).forEach(([k, v]) => { walk(v, k, depth + 1, childParentIds); });
            }
            nodes.push({id: idCounter++, depth, key: null, type: isArray ? 'array-close' : 'object-close', closeChar: isArray ? ']' : '}', pairId: myId, parentIds: [...parentIds, myId]});
        };

        walk(data, null, 0, []);
        return nodes;
    };

    isVisible = (node) => {
        return node.parentIds.every(pid => !this.collapsedIds.has(pid));
    };

    toggleNode = (node) => {
        const next = new Set(this.collapsedIds);
        if (next.has(node.id)) { next.delete(node.id); }
        else { next.add(node.id); }
        this.collapsedIds = next;
    };

    getValueColor = (valueType) => {
        if (valueType === 'string') { return '#0a3069'; }
        if (valueType === 'number') { return '#953800'; }
        if (valueType === 'boolean') { return '#8250df'; }
        return '#57606a';
    };

    formatValue = (node) => {
        if (node.valueType === 'string') { return `"${node.value}"`; }
        if (node.value === null) { return 'null'; }
        return String(node.value);
    };
}
